import { NavLink, Outlet, Navigate } from "react-router-dom";
import {
  HomeIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  LifebuoyIcon,
  ArrowRightStartOnRectangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useAuthStore } from "../stores/auth";
import { isInternalEmail } from "./admin/isAdmin";

const sidebarLinks = [
  { to: "/portal/dashboard", icon: HomeIcon, label: "Dashboard" },
  { to: "/portal/downloads", icon: ArrowDownTrayIcon, label: "Downloads" },
  { to: "/portal/settings", icon: Cog6ToothIcon, label: "Settings" },
  { to: "/help", icon: LifebuoyIcon, label: "Help" },
];

export function PortalLayout() {
  const { session, user, schoolName, initialized, signOut } = useAuthStore();

  if (!initialized) {
    return (
      <div className="workspace-shell flex min-h-[calc(100vh-5rem)] items-center justify-center bg-cream-100/50">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest-300 border-t-forest-700" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/portal/login" replace />;
  }

  const showAdmin = isInternalEmail(user?.email);

  return (
    <div className="workspace-shell flex min-h-[calc(100vh-5rem)]">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-shrink-0 border-r border-forest-800/10 bg-forest-950 lg:block">
        <div className="flex h-full flex-col">
          {/* School info */}
          <div className="border-b border-cream-200/10 p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold-200">
              School Portal
            </p>
            <p className="mt-1 truncate font-display text-sm font-medium text-cream-100">
              {schoolName}
            </p>
          </div>

          {/* Nav links */}
          <nav className="flex-1 p-3">
            <ul className="space-y-1">
              {sidebarLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-cream-100 text-forest-900"
                          : "text-cream-200/90 hover:bg-forest-900 hover:text-cream-200"
                      }`
                    }
                  >
                    <link.icon className="h-5 w-5" />
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            {showAdmin && (
              <div className="mt-6 border-t border-cream-200/10 pt-4">
                <p className="px-3 text-xs font-semibold uppercase tracking-widest text-gold-200">
                  Admin
                </p>
                <ul className="mt-2 space-y-1">
                  <li>
                    <NavLink
                      to="/admin"
                      end
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-cream-100 text-forest-900"
                            : "text-cream-200/90 hover:bg-forest-900 hover:text-cream-200"
                        }`
                      }
                    >
                      <ShieldCheckIcon className="h-5 w-5" />
                      Open admin panel
                    </NavLink>
                  </li>
                </ul>
              </div>
            )}
          </nav>

          {/* Logout */}
          <div className="border-t border-cream-200/10 p-3">
            <button
              onClick={signOut}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-cream-200/80 transition-colors hover:bg-forest-900 hover:text-cream-200"
            >
              <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main portal content */}
      <main className="workspace-content min-w-0 flex-1 bg-cream-100/50 pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav aria-label="Portal navigation" className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-forest-800/10 bg-forest-950 pb-[env(safe-area-inset-bottom)]">
        <div className={`grid items-center ${showAdmin ? "grid-cols-6" : "grid-cols-5"}`}>
          {sidebarLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `flex min-w-0 flex-col items-center gap-1 whitespace-nowrap px-1 py-2.5 text-[10px] font-medium transition-colors sm:text-xs ${
                  isActive
                    ? "text-cream-100"
                    : "text-cream-200/80"
                }`
              }
            >
              <link.icon className="h-5 w-5" />
              {link.to === "/portal/dashboard" ? "Home" : link.label}
            </NavLink>
          ))}
          {showAdmin && (
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                `flex min-w-0 flex-col items-center gap-1 whitespace-nowrap px-1 py-2.5 text-[10px] font-medium transition-colors sm:text-xs ${
                  isActive ? "text-cream-100" : "text-cream-200/80"
                }`
              }
            >
              <ShieldCheckIcon className="h-5 w-5" />
              Admin
            </NavLink>
          )}
          <button
            onClick={signOut}
            className="flex min-w-0 flex-col items-center gap-1 whitespace-nowrap px-1 py-2.5 text-[10px] font-medium text-cream-200/80 transition-colors sm:text-xs"
          >
            <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
            Sign out
          </button>
        </div>
      </nav>
    </div>
  );
}
